import { useCallback, useRef } from 'react';
import type { ViewStyle } from 'react-native';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import type {
  ICarouselInstance,
  TCarouselProps
} from 'react-native-reanimated-carousel';
import Carousel from 'react-native-reanimated-carousel';

import { CarouselWrapper } from '@/shared/components/carousel/CarouselWrapper';
import { DotPagination } from '@/shared/components/carousel/DotPagination';
import { useCarouselLayout } from '@/shared/hooks/useCarouselLayout';
import type { CarouselLayoutSize } from '@/shared/types/carousel';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props<T> = {
  renderItem: (
    item: T,
    layout: CarouselLayoutSize,
    index?: number
  ) => React.ReactElement;
  style?: ViewStyle;
  /**
   * Horizontal gap in px between adjacent items, and between the outermost
   * items and the carousel edges. Implemented as per-item padding so the
   * carousel still pages by the full layout width. @default 0
   */
  itemGap?: number;
} & Omit<TCarouselProps<T>, 'renderItem'>;

export const FixedSizeCarousel = <T,>({
  renderItem,
  style,
  itemGap = 0,
  ...carouselProps
}: Props<T>) => {
  const { colors } = useAppTheme();

  const { layout, onLayout } = useCarouselLayout();

  const progress = useSharedValue<number>(0);

  const ref = useRef<ICarouselInstance>(null);

  const handlePaginationPress = (index: number) => {
    ref.current?.scrollTo({
      count: index - progress.get(),
      animated: true
    });
  };

  const handleRenderItem = useCallback(
    (item: T, index: number) => {
      const isFirst = index === 0;
      const isLast = index === carouselProps.data.length - 1;
      const paddingLeft = isFirst ? itemGap : itemGap / 2;
      const paddingRight = isLast ? itemGap : itemGap / 2;
      // The wrapper takes the full paging-slot width and applies the gap as
      // padding; the child fills the remaining content box, so we must NOT
      // pass the raw layout down — otherwise the child would overflow the
      // padding.
      return (
        <View style={[layout, { paddingLeft, paddingRight }]}>
          {renderItem(
            item,
            {
              width: layout.width - paddingLeft - paddingRight,
              height: layout.height
            },
            index
          )}
        </View>
      );
    },
    [layout, itemGap, carouselProps.data.length, renderItem]
  );

  return (
    <>
      <CarouselWrapper
        style={style}
        onLayout={onLayout}
        progress={progress}
        size={carouselProps.data.length}
        onPressLeft={handlePaginationPress}
        onPressRight={handlePaginationPress}
      >
        {layout.width && layout.height && (
          // @ts-expect-error Omit causes type mismatch
          <Carousel<T>
            {...carouselProps}
            renderItem={({ item, index }) => handleRenderItem(item, index)}
            ref={ref}
            onProgressChange={progress}
            snapEnabled={carouselProps.snapEnabled ?? true}
            pagingEnabled={carouselProps.pagingEnabled ?? true}
            loop={carouselProps.loop ?? false}
            {...layout}
          />
        )}
      </CarouselWrapper>

      <DotPagination
        color={colors.primary}
        progress={progress}
        size={carouselProps.data.length}
        onPress={handlePaginationPress}
      />
    </>
  );
};
